import LikertSlider from './LikertSlider.jsx'

// 2- oder 3-Optionen-Vergleich mit Likert-Slider darunter.
// options: [{ text, image?, imagePending? }, ...]
//   image:        URL → Bild rendern
//   imagePending: true → Bildbox rendern, aber leer (Bild noch nicht geliefert)
// scale: 'twoway' | 'threeway'
// value: 0 (noch nicht gewählt) oder 1..5
export default function ComparisonStep({ prompt, options, scale, value, onChange, scaleLabels, hint }) {
  // Bilder/Texte müssen IMMER nebeneinander bleiben (auch mobil), damit die Vergleichs-Logik
  // mit dem Slider darunter erhalten bleibt — sonst kippt es auf schmalen Screens zu Stapeln.
  const cols = options.length === 3 ? 'grid-cols-3' : 'grid-cols-2'
  const labels = scaleLabels[scale] || scaleLabels.twoway
  const hasValue = value > 0
  const hasImageSlot = (o) => o.image || o.imagePending
  const allTextOnly = options.every(o => !hasImageSlot(o))
  // Bei 3 Spalten ist pro Karte deutlich weniger Platz — dort konservativere Schrift,
  // sonst ragen lange Wörter ("Synchronschwimmer:innen") raus.
  const textSize = options.length === 3
    ? 'text-xs sm:text-sm md:text-base lg:text-lg'
    : 'text-sm sm:text-base md:text-lg lg:text-xl'
  const captionSize = options.length === 3
    ? 'text-[11px] sm:text-xs md:text-sm'
    : 'text-xs md:text-sm'

  // Klick auf Karte schiebt den Slider einen Tick Richtung Karte. Startwert (noch nicht gewählt) = 3.
  // 2-Way: links → −1, rechts → +1.
  // 3-Way: jede Karte hat ein Ziel (1, 3, 5); Klick bewegt einen Tick in Richtung des Ziels.
  // Sonderfall 3-Way: Erstklick auf die mittlere Karte (target===3, noch kein Wert) setzt
  // direkt 3 — sonst würde der Klick ignoriert (current===target) und nichts markiert.
  const handleCardClick = (index) => {
    const current = value || 3
    if (options.length === 2) {
      onChange(index === 0 ? Math.max(1, current - 1) : Math.min(5, current + 1))
      return
    }
    const target = [1, 3, 5][index]
    if (current === target) {
      if (!hasValue) onChange(target)
      return
    }
    onChange(current < target ? Math.min(target, current + 1) : Math.max(target, current - 1))
  }

  return (
    <div className="space-y-8">
      <div className="text-base font-medium text-ink leading-relaxed">{prompt}</div>

      <div className={`grid ${cols} gap-2 sm:gap-3 md:gap-4`}>
        {options.map((opt, i) => {
          const opacity = hasValue ? optionOpacity(i, options.length, value) : 1
          const ariaLabel = `${opt.text} – Slider Richtung dieser Option bewegen`
          if (hasImageSlot(opt)) {
            return (
              <button
                key={i}
                type="button"
                onClick={() => handleCardClick(i)}
                aria-label={ariaLabel}
                className="flex flex-col items-stretch justify-start space-y-2 transition-opacity duration-300 min-w-0 text-left cursor-pointer group focus:outline-none"
                style={{ opacity }}
              >
                <div className="aspect-square rounded-xl border border-teal-100 bg-teal-50/30 overflow-hidden grid place-items-center p-2 md:p-4 transition group-hover:border-teal-400 group-hover:bg-teal-50 group-focus-visible:ring-2 group-focus-visible:ring-teal-400">
                  {opt.image
                    ? <img src={opt.image} alt={opt.text} className="max-w-full max-h-full object-contain pointer-events-none" />
                    : <span className="text-ink-mute text-center px-2">Bild muss nachgereicht werden</span>}
                </div>
                <div
                  lang="de"
                  className={`text-center ${captionSize} text-ink-soft leading-snug px-1 md:px-2 hyphens-auto [overflow-wrap:anywhere] transition group-hover:text-ink`}
                >
                  {opt.text}
                </div>
              </button>
            )
          }
          // Text-only option: card with prominent text, no aspect-square (so it doesn't look like an empty image slot)
          return (
            <button
              key={i}
              type="button"
              onClick={() => handleCardClick(i)}
              aria-label={ariaLabel}
              className={`rounded-xl border border-teal-100 bg-teal-50/30 grid place-items-center text-center transition min-w-0 overflow-hidden cursor-pointer hover:border-teal-400 hover:bg-teal-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 ${
                allTextOnly ? 'min-h-[100px] sm:min-h-[120px] md:min-h-[140px] p-2 sm:p-3 md:p-6' : 'aspect-square p-2 sm:p-3 md:p-6'
              }`}
              style={{ opacity }}
            >
              <div
                lang="de"
                className={`font-semibold text-ink leading-snug ${textSize} hyphens-auto [overflow-wrap:anywhere] w-full`}
              >
                {opt.text}
              </div>
            </button>
          )
        })}
      </div>

      <div className="pt-2">
        <div className="text-sm font-medium mb-3">{hint}</div>
        <LikertSlider value={value} onChange={onChange} labels={labels} />
      </div>
    </div>
  )
}

// Berechne Opazität für Option i.
// 2-Way: nur die "Verlierer-Seite" fadet; Mitte (value=3) = beide voll
// 3-Way: jede Option hat ein Ziel (1, 3, 5); Distanz bestimmt Fade-Stärke
function optionOpacity(index, total, value) {
  if (total === 2) {
    // 1 Tick weg von neutral ("wichtig") → schon spürbar transparent;
    // 2 Ticks ("sehr wichtig") → stark transparent. Index = Distanz auf der Verlierer-Seite.
    const FADES_2WAY = [1.0, 0.45, 0.12]
    if (index === 0) {
      const dist = Math.max(0, value - 3)
      return FADES_2WAY[dist]
    }
    const dist = Math.max(0, 3 - value)
    return FADES_2WAY[dist]
  }
  if (total === 3) {
    // Targets: i=0 → 1, i=1 → 3, i=2 → 5. Max-Distanz = 4 → Min-Opazität ~0.12
    const FADE_MAX = 0.88
    const targets = [1, 3, 5]
    const dist = Math.abs(value - targets[index])
    return 1 - FADE_MAX * (dist / 4)
  }
  return 1
}
